import { useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { client } from '@frontend/features/common/services';
import { catalogAdminKey, useCatalogAdminCapability } from './queries';
import { HotelPreparation } from './HotelPreparation';
interface PreparationSource {
  supplier_hotel_id: number | null;
  connector: string;
  connector_account_id: string;
  supplier_hotel_code: string;
  access_code: string | null;
  supplier_code: string | null;
}
export function ResumeHotelPreparation({ hotelId }: { hotelId: string }) {
  const { t } = useTranslation();
  const capability = useCatalogAdminCapability();
  const [opened, setOpened] = useState(false);
  const [sourceId, setSourceId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const sources = useQuery(
    [...catalogAdminKey, 'preparation-sources', hotelId],
    async () =>
      (
        await client.get<{ data: PreparationSource[] }>(
          `hotel-catalog-admin/hotels/${encodeURIComponent(hotelId)}/preparation-sources`,
        )
      ).data.data,
    { enabled: opened && !!hotelId && capability.data?.allowed === true, retry: false },
  );
  if (!hotelId || !capability.data?.allowed) return null;
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5">{t('hotelResume.title')}</Typography>
      {!opened ? (
        <Button onClick={() => setOpened(true)}>{t('hotelResume.open')}</Button>
      ) : (
        <Stack spacing={2}>
          <Alert severity="info">{t('hotelResume.help')}</Alert>
          {sources.isFetching && <Typography>{t('hotelEditorial.loading')}</Typography>}
          {sources.isError && <Alert severity="warning">{t('hotelResume.loadError')}</Alert>}
          {!sources.isFetching && !sources.isError && sources.data?.length === 0 && (
            <Alert severity="info">{t('hotelResume.empty')}</Alert>
          )}
          {sources.data?.some((s) => s.supplier_hotel_id === null) && (
            <Alert severity="warning">{t('hotelResume.legacy')}</Alert>
          )}
          {(sources.data ?? []).map((source, index) => (
            <Button
              key={index}
              disabled={busy || sources.isFetching || sources.isError || source.supplier_hotel_id === null}
              variant={sourceId !== null && sourceId === source.supplier_hotel_id ? 'contained' : 'outlined'}
              onClick={() => setSourceId(source.supplier_hotel_id)}
            >
              {source.connector} · {source.connector_account_id} · {source.supplier_code} / {source.access_code} /{' '}
              {source.supplier_hotel_code} ({source.supplier_hotel_id ?? '—'})
            </Button>
          ))}
          <Button
            disabled={busy || sources.isFetching}
            onClick={() => {
              setSourceId(null);
              sources.refetch();
            }}
          >
            {t('hotelCatalogAdmin.reload')}
          </Button>
          {sourceId !== null && (
            <HotelPreparation
              key={`${hotelId}:${sourceId}`}
              hotelId={hotelId}
              sourceId={sourceId}
              onBusyChange={setBusy}
            />
          )}
        </Stack>
      )}
    </Box>
  );
}
